import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  Modal,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  getDashboardControllerHausmeisterWartelisteQueryKey,
  useDashboardControllerHausmeisterWarteliste,
  useOrdersControllerAddAttachment,
  type ApiError,
  type HausmeisterWartelisteDtoOutputItem,
} from '@werkbank/shared';
import { formatCents, formatDateDE } from '../lib/format';
import { notify } from '../lib/notify';
import { EmptyState } from './EmptyState';

type Zeile = HausmeisterWartelisteDtoOutputItem;

const ERLAUBT = ['application/pdf', 'image/jpeg', 'image/png'] as const;

async function alsBase64(datei: File): Promise<string> {
  const bytes = new Uint8Array(await datei.arrayBuffer());
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Tobias 2026-10-04: Mieterdirekt-Aufträge über der Schwelle gehen an den
 * Hausmeister — abgerechnet wird erst mit seinem unterschriebenen
 * Arbeitsnachweis. Die Karte zeigt, worauf noch gewartet wird, und nimmt das
 * per Mail zurückgekommene, unterschriebene Blatt direkt entgegen (Upload als
 * Auftrags-Anhang mit „unterschriebener Arbeitsnachweis"). Danach verschwindet
 * die Zeile, und die Rechnung lässt sich festschreiben.
 */
export function HausmeisterWartelisteCard() {
  const liste = useDashboardControllerHausmeisterWarteliste<Zeile[], ApiError>();
  const [fuer, setFuer] = useState<Zeile | null>(null);
  const zeilen = liste.data ?? [];

  return (
    <Card padding={24} data-testid="hausmeister-warteliste">
      <Group justify="space-between" align="baseline" mb="sm">
        <Text fw={600}>
          Warten auf Hausmeister-Unterschrift{' '}
          <Text span c="dimmed" fw={400}>
            ({zeilen.length})
          </Text>
        </Text>
        <Text size="xs" c="dimmed">
          Festgeschriebene Arbeitsnachweise · Mieterdirektbeauftragung über der Schwelle
        </Text>
      </Group>
      {liste.isLoading ? null : zeilen.length === 0 ? (
        <EmptyState text="Keine offenen Unterschriften." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table className="wb-table-dense" verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nachweis</Table.Th>
                <Table.Th>Auftrag</Table.Th>
                <Table.Th>Objekt / Mieter</Table.Th>
                <Table.Th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  Wert (brutto)
                </Table.Th>
                <Table.Th style={{ whiteSpace: 'nowrap' }}>Festgeschrieben</Table.Th>
                <Table.Th>Wartet</Table.Th>
                <Table.Th aria-label="Aktion" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {zeilen.map((z) => (
                <Table.Tr key={z.orderId} data-testid={`hausmeister-zeile-${z.orderNumber}`}>
                  <Table.Td className="wb-tnum">
                    <Link to="/documents/$documentId" params={{ documentId: z.workReportId }}>
                      {z.workReportNumber}
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <Link to="/orders/$orderId" params={{ orderId: z.orderId }}>
                      {z.orderNumber}
                    </Link>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {z.orderTitle}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{z.ort ?? '—'}</Text>
                    {z.mieter && (
                      <Text size="xs" c="dimmed">
                        {z.mieter}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td
                    className="wb-tnum"
                    style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    {formatCents(z.wertCents)} €
                  </Table.Td>
                  <Table.Td className="wb-tnum">{formatDateDE(z.festgeschriebenAm)}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    <Badge
                      styles={{ label: { overflow: 'visible' } }}
                      size="sm"
                      variant="light"
                      color={z.wartetTage > 14 ? 'red' : z.wartetTage > 7 ? 'orange' : 'gray'}
                    >
                      {z.wartetTage === 0 ? 'heute' : `${z.wartetTage} Tage`}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size="compact-sm"
                      variant="light"
                      onClick={() => setFuer(z)}
                      data-testid={`hausmeister-upload-${z.orderNumber}`}
                    >
                      Unterschrift hochladen
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
      {fuer && <UploadDialog zeile={fuer} onClose={() => setFuer(null)} />}
    </Card>
  );
}

function UploadDialog({ zeile, onClose }: { zeile: Zeile; onClose: () => void }) {
  const qc = useQueryClient();
  const [datei, setDatei] = useState<File | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const hochladen = useOrdersControllerAddAttachment<ApiError>({
    mutation: {
      onSuccess: () => {
        notify.success(`Unterschriebener Nachweis zu ${zeile.orderNumber} hinterlegt.`);
        void qc.invalidateQueries({
          queryKey: getDashboardControllerHausmeisterWartelisteQueryKey(),
        });
        onClose();
      },
      onError: (e) => setFehler(e.message),
    },
  });
  const speichern = async () => {
    if (!datei) return;
    if (!ERLAUBT.includes(datei.type as (typeof ERLAUBT)[number])) {
      setFehler('Bitte eine PDF-, JPG- oder PNG-Datei wählen.');
      return;
    }
    hochladen.mutate({
      id: zeile.orderId,
      data: {
        filename: datei.name,
        mimeType: datei.type as (typeof ERLAUBT)[number],
        contentBase64: await alsBase64(datei),
        signedWorkReport: true,
        attachToInvoice: true,
      },
    });
  };
  return (
    <Modal opened onClose={onClose} title="Unterschriebenen Arbeitsnachweis hochladen">
      <Stack gap="md">
        <Text size="sm">
          {zeile.workReportNumber} · Auftrag {zeile.orderNumber}
          {zeile.mieter ? ` · ${zeile.mieter}` : ''}
        </Text>
        <FileInput
          label="Datei vom Hausmeister (PDF, JPG oder PNG)"
          placeholder="Datei wählen"
          accept={ERLAUBT.join(',')}
          value={datei}
          onChange={(f) => {
            setFehler(null);
            setDatei(f);
          }}
          clearable
          data-testid="hausmeister-datei"
        />
        <Text size="xs" c="dimmed">
          Die Datei wird am Auftrag als „Arbeitsnachweis (Papier)“ gespeichert und beim
          Festschreiben der Rechnung als letzte Seite angehängt.
        </Text>
        {fehler && (
          <Alert color="red" variant="light">
            {fehler}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void speichern()}
            loading={hochladen.isPending}
            disabled={!datei}
            data-testid="hausmeister-speichern"
          >
            Als unterschrieben hinterlegen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
